import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Public Pages
import Home from "./pages/Home";
import About from "./pages/About";
import News from "./pages/News";
import NewsDetail from "./pages/NewsDetail";
import Practitioners from "./pages/Practitioners";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Education from "./pages/Education";
import Events from "./pages/Events";
import { MembershipDoctors, MembershipStudents } from "./pages/Membership";

// Protected Pages (Member Area)
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import ChangePassword from "./pages/ChangePassword";
import Documents from "./pages/Documents";
import MyEvents from "./pages/MyEvents";
import EventCheckIn from "./pages/EventCheckIn";
import AdminDashboard from "./pages/AdminDashboard";
import NewsManagement from "./pages/NewsManagement";
import EventsManagement from "./pages/EventsManagement";
import EventScanner from "./pages/admin/EventScanner";
import BrandManagement from "./pages/admin/BrandManagement";
import CampaignManagement from "./pages/admin/CampaignManagement";
import CreativeManagement from "./pages/admin/CreativeManagement";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import CouncilManagement from "./pages/admin/CouncilManagement";
import EducationManagement from "./pages/admin/EducationManagement";
import ExecutiveMembers from "./pages/ExecutiveMembers";
import MeetingRequest from "./pages/executive/MeetingRequest";
import ManagementGuide from "./pages/executive/ManagementGuide";
import MeetingMinutes from "./pages/executive/MeetingMinutes";
import Financials from "./pages/executive/Financials";
import Tasks from "./pages/executive/Tasks";
import Reports from "./pages/executive/Reports";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-grow">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/practitioners" element={<Practitioners />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/education" element={<Education />} />
                <Route path="/education/:category" element={<Education />} />
                <Route path="/events" element={<Events />} />
                <Route path="/membership" element={<MembershipDoctors />} />
                <Route path="/membership/doctors" element={<MembershipDoctors />} />
                <Route path="/membership/students" element={<MembershipStudents />} />
                
                {/* Protected Routes (Require Authentication) */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/change-password" 
                  element={
                    <ProtectedRoute requireAuth={true} allowPasswordChange={true}>
                      <ChangePassword />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/documents" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <Documents />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/my-events" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <MyEvents />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/event-checkin/:eventId" 
                  element={
                    <ProtectedRoute requireAuth={true}>
                      <EventCheckIn />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Admin Routes (Require Admin/Executive Role) */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/news" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <NewsManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/events" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <EventsManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/event-scanner" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <EventScanner />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/brands" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <BrandManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/campaigns" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <CampaignManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/creatives" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <CreativeManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/analytics" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <AnalyticsDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/council" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <CouncilManagement />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/education" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="admin">
                      <EducationManagement />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Executive Routes (Require Executive Role) */}
                <Route 
                  path="/executive" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <ExecutiveMembers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/meeting-request" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <MeetingRequest />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/management-guide" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <ManagementGuide />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/meeting-minutes" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <MeetingMinutes />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/financials" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <Financials />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/tasks" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <Tasks />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/executive/reports" 
                  element={
                    <ProtectedRoute requireAuth={true} requireRole="executive">
                      <Reports />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
